import { useContext, useEffect } from 'react';
import { addDays, isAfter } from 'date-fns';

import { ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useIsEstablishedUserTraitName } from '@confluence/onboarding-helpers/entry-points/hooks/useIsEstablishedUserTraitName.ts';
import { useExperimentalUserPersonalizationQuery } from '@confluence/onboarding-helpers/entry-points/hooks/useExperimentalUserPersonalizationQuery.ts';
import { fg } from '@confluence/feature-gating';

const HOME_ONBOARDING_TAKEOVER_NTH_USER_DAYS = 14;

/**
 * Custom hook to determine the eligibility for the Nth user Home Onboarding Takeover.
 *
 * This hook checks multiple conditions to determine if a user is eligible for the onboarding experience.
 * The conditions include:
 * 1. Whether the user joins a free/paid instance
 * 2. Whether the user is a nth user(not a founding admin).
 * 4. Whether the user is new(not an established user)
 * 5. Whether the user has feature gate enabled
 *
 * @returns {boolean} - Returns `true` if the user passes all eligibility checks, otherwise `false`.
 */
export const useNthUserHomeOnboardingTakeoverV2Eligibility = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { accessStatus, userId } = useSessionData();
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();

	// User is licensed (site admin or nth user)
	const hasAdminOrUseAccess =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS;

	useEffect(() => {
		experienceTracker.start({
			name: ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER,
		});
	}, [experienceTracker]);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
		],
		!hasAdminOrUseAccess,
	);
	const { isFoundingAdmin, takeoverSeenAt } = deserializeState(onboardingStateData);

	const takeoverExpired =
		Boolean(takeoverSeenAt) &&
		isAfter(Date.now(), addDays(new Date(takeoverSeenAt), HOME_ONBOARDING_TAKEOVER_NTH_USER_DAYS));

	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useExperimentalUserPersonalizationQuery({
		skip: !hasAdminOrUseAccess || takeoverExpired,
		variables: {
			userId: userId!,
		},
	});

	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];
	const isEstablishedUser =
		attributes.find(
			(attr: { name: string; value: string }) => attr?.name === isEstablishedUserTraitName,
		)?.value === 'true';

	const isEstablishedUserReady = !userPersonalizationQueryLoading && !userPersonalizationQueryError;

	const isNotEstablishedUser = isEstablishedUserReady && !isEstablishedUser;

	const isEligible = Boolean(
		hasAdminOrUseAccess && (takeoverSeenAt ?? isNotEstablishedUser) && !isFoundingAdmin,
	);

	if (onboardingStateError || userPersonalizationQueryError) {
		experienceTracker.stopOnError({
			name: ONBOARDING_NTH_USER_HOME_ONBOARDING_TAKEOVER,
			error: (onboardingStateError || userPersonalizationQueryError)!,
		});
	}

	if (fg('hot-122822-hide-nth-user-onboarding')) {
		return {
			isEligible: false,
			isVisible: false,
			loading: false,
		};
	}
	return {
		isEligible,
		isVisible: isEligible && !takeoverExpired,
		loading: onboardingStateLoading || userPersonalizationQueryLoading,
	};
};
